import { useEffect, useRef } from 'react'
import { useAppToast } from '@ui-seeker/app'
import { getSubmit, postSubmit } from '@ui-seeker/utils'
import { BACKEND_APIS } from '../../../@constants/routes'
import {
    tableData,
    totalCount,
    isLoadingTable,
    latestId,
    autoRefresh,
    refreshInterval,
    lastRefreshTime,
    nextRefreshCountdown,
    filterProductId,
    filterIdSites,
    filterIdSku,
    filterUserId,
    filterAction,
    filterMappingType,
    filterSource,
    filterDateFrom,
    filterDateTo,
    excludePropagated,
    currentPage,
    pageSize,
    sourcesOptions,
    sitesOptions,
    userAnalysisData,
    userActivityData,
    isLoadingUserAnalysis,
    selectedUserForAnalysis,
    applyQuickFilter
} from '../@signals'

export default function useAuditLog() {
    const { showToast } = useAppToast()
    const intervalRef = useRef(null)
    const countdownRef = useRef(null)
    const initializedRef = useRef(false)

    // Fetch audit data with filters
    const fetchAuditData = (showLoading = true) => {
        if (showLoading) {
            isLoadingTable.value = true
        }

        const filters = {
            product_id: filterProductId.value || undefined,
            id_sites: filterIdSites.value.length > 0 ? filterIdSites.value.map(s => s.value) : undefined,
            id_sku: filterIdSku.value || undefined,
            user_id: filterUserId.value || undefined,
            action: filterAction.value.length > 0 ? filterAction.value : undefined,
            mapping_type: filterMappingType.value.length > 0 ? filterMappingType.value : undefined,
            source: filterSource.value.length > 0 ? filterSource.value.map(s => s.value) : undefined,
            date_from: filterDateFrom.value || undefined,
            date_to: filterDateTo.value || undefined,
            exclude_propagated: excludePropagated.value || undefined,
            page: currentPage.value,
            pageSize: pageSize.value
        }

        // Remove undefined values
        const cleanFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, v]) => v !== undefined)
        )

        postSubmit(`${BACKEND_APIS.AUDIT_LOG}/search`, cleanFilters)
            .then(res => {
                if (res) {
                    tableData.value = res.data || []
                    totalCount.value = res.total || 0
                    lastRefreshTime.value = new Date()

                    // Update latestId for streaming
                    if (res.data?.length > 0) {
                        const maxId = Math.max(...res.data.map(r => r.id_audit))
                        latestId.value = maxId
                    }
                }
            })
            .catch(err => {
                console.error('Error fetching audit data:', err)
                showToast.error('Error loading audit data')
            })
            .finally(() => {
                isLoadingTable.value = false
            })
    }

    // Fetch new records for streaming (after latestId). The live stream
    // honours the action/source filters the operator has active, so watching
    // "what is process X mapping right now" actually works — without this the
    // stream prepended EVERY new record regardless of filters.
    const fetchNewRecords = () => {
        if (!autoRefresh.value) return

        getSubmit(`${BACKEND_APIS.AUDIT_LOG}/stream`, {
            after_id: latestId.value,
            limit: 50,
            action: filterAction.value.length > 0 ? filterAction.value.join(',') : undefined,
            source: filterSource.value.length > 0 ? filterSource.value.map(s => s.value).join(',') : undefined,
        })
            .then(res => {
                if (res?.data?.length > 0) {
                    // Prepend new records to existing data
                    tableData.value = [...res.data, ...tableData.value]
                    totalCount.value = totalCount.value + res.data.length
                    latestId.value = res.latest_id
                    lastRefreshTime.value = new Date()
                }
            })
            .catch(err => {
                console.error('Error fetching new records:', err)
            })
    }

    const fetchFilterOptions = () => {
        getSubmit(`${BACKEND_APIS.AUDIT_LOG}/sources`)
            .then(res => { if (res) sourcesOptions.value = res })
            .catch(err => console.error('Error fetching sources:', err))

        getSubmit(`${BACKEND_APIS.AUDIT_LOG}/sites`)
            .then(res => { if (res) sitesOptions.value = res })
            .catch(err => console.error('Error fetching sites:', err))
    }

    // Fetch user analysis data
    const fetchUserAnalysis = (userId, days = 30) => {
        if (!userId) return

        isLoadingUserAnalysis.value = true

        Promise.all([
            getSubmit(`${BACKEND_APIS.AUDIT_LOG}/user/${userId}/stats`, { days }),
            getSubmit(`${BACKEND_APIS.AUDIT_LOG}/user/${userId}/activity`, { days })
        ])
            .then(([stats, activity]) => {
                userAnalysisData.value = stats
                userActivityData.value = activity?.activity || []
            })
            .catch(err => {
                console.error('Error fetching user analysis:', err)
                showToast.error('Error loading user analysis')
            })
            .finally(() => {
                isLoadingUserAnalysis.value = false
            })
    }

    // Initial load - only once
    useEffect(() => {
        if (initializedRef.current) return
        initializedRef.current = true

        // Apply default quick filter (today)
        applyQuickFilter('today')
        fetchFilterOptions()
        fetchAuditData()
    }, [])

    // Setup auto-refresh interval
    useEffect(() => {
        // Clear existing intervals
        if (intervalRef.current) clearInterval(intervalRef.current)
        if (countdownRef.current) clearInterval(countdownRef.current)

        if (autoRefresh.value) {
            // Reset countdown to full interval
            nextRefreshCountdown.value = refreshInterval.value / 1000

            // Single interval that handles both countdown and refresh
            countdownRef.current = setInterval(() => {
                const newValue = nextRefreshCountdown.value - 1
                if (newValue <= 0) {
                    // Time to refresh
                    fetchNewRecords()
                    nextRefreshCountdown.value = refreshInterval.value / 1000
                } else {
                    nextRefreshCountdown.value = newValue
                }
            }, 1000)
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
            if (countdownRef.current) clearInterval(countdownRef.current)
        }
    }, [autoRefresh.value])

    // Watch for user selection changes
    useEffect(() => {
        if (selectedUserForAnalysis.value) {
            fetchUserAnalysis(selectedUserForAnalysis.value)
        }
    }, [selectedUserForAnalysis.value])

    return {
        fetchAuditData,
        fetchNewRecords,
        fetchUserAnalysis,
        toggleAutoRefresh: () => {
            autoRefresh.value = !autoRefresh.value
            if (autoRefresh.value) {
                nextRefreshCountdown.value = refreshInterval.value / 1000
            }
        }
    }
}
